/**
 * Device identity and the "who is running this" profile kept on this phone.
 *
 * - Device id: a stable uuid in localStorage (same approach as
 *   frontend/ccms_resource_centre/src/utils/anonymousId.ts). Sent as `deviceId` on public runs and chat.
 * - Chat session key: one uuid per procedure per device, so "Ask" keeps its conversation.
 * - Runner profile: the name and ministry an anonymous volunteer typed once, prefilled next time.
 */
import { uuid } from '@/utils/uuid';
import { readJson, readText, writeJson, writeText } from './storage';

const DEVICE_KEY = 'procedures.device_id';
const RUNNER_KEY = 'procedures.runner';
const chatKey = (code: string) => `procedures.chat.${code.toLowerCase()}`;

/** Kept for the page load when storage is blocked, so every call still returns the same id. */
let memoryDeviceId: string | null = null;

export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  const existing = readText(DEVICE_KEY);
  if (existing) return existing;
  if (!memoryDeviceId) memoryDeviceId = uuid();
  writeText(DEVICE_KEY, memoryDeviceId);
  return memoryDeviceId;
}

const memoryChatKeys = new Map<string, string>();

/** The chat session for this procedure on this device (created on first use). */
export function getChatSessionKey(code: string): string {
  const key = chatKey(code);
  const existing = readText(key);
  if (existing) return existing;
  const fresh = memoryChatKeys.get(key) ?? uuid();
  memoryChatKeys.set(key, fresh);
  writeText(key, fresh);
  return fresh;
}

/** Start a fresh conversation for this procedure ("Start a new chat"). */
export function resetChatSessionKey(code: string): string {
  const fresh = uuid();
  memoryChatKeys.set(chatKey(code), fresh);
  writeText(chatKey(code), fresh);
  return fresh;
}

export interface RunnerProfile {
  name: string;
  /** A CCMS ministry, or null when "Other" (see ministryOther) or not chosen. */
  ministryID: number | null;
  ministryOther: string | null;
}

export function getRunnerProfile(): RunnerProfile | null {
  const p = readJson<RunnerProfile>(RUNNER_KEY);
  if (!p || typeof p.name !== 'string') return null;
  return {
    name: p.name,
    ministryID: typeof p.ministryID === 'number' ? p.ministryID : null,
    ministryOther: typeof p.ministryOther === 'string' ? p.ministryOther : null,
  };
}

export function saveRunnerProfile(profile: RunnerProfile): void {
  writeJson(RUNNER_KEY, profile);
}
